import { AddToCart } from './AddToCart';
import React from 'preact/compat';
import ShopifyBuy, { EmbarkProduct } from 'shopify-buy';
import { notifyHandledException } from '../../utils/errorLogger';
import { formatMoneyWithConditionalCents } from '../../utils/currency';

interface Props {
  product: EmbarkProduct;
  variant: ShopifyBuy.ProductVariant;
  onChange?: () => void;
  sellingPlanId?: string;
  discountCode?: string;
}

function getImage(
  product: EmbarkProduct,
  variant?: ShopifyBuy.ProductVariant
): ShopifyBuy.Image {
  if (!variant) {
    return product.featuredImage;
  }

  return variant.node.image || product.featuredImage;
}

function getPrice(variant: ShopifyBuy.ProductVariant): [string, string] {
  if (!variant) {
    return ['', ''];
  }

  // variant.price is not consistent with product.price,
  // instead of cents variant.price is in dollars.
  let displayPrice = (variant.node.price.amount * 100).toString();

  let compareAtPrice = variant.node?.compareAtPrice
    ? (variant.node.compareAtPrice.amount * 100).toString()
    : '';

  if (variant.node?.displayPrice?.value) {
    displayPrice = variant.node.displayPrice.value;
    compareAtPrice = (variant.node.price.amount * 100).toString();
  }

  if (
    window.Shopify.formatMoney(parseFloat(displayPrice)) ===
    window.Shopify.formatMoney(parseFloat(compareAtPrice))
  ) {
    compareAtPrice = '';
  }

  if (compareAtPrice && parseFloat(displayPrice) > parseFloat(compareAtPrice)) {
    notifyHandledException(
      new Error(
        `Price Alert: Display price is greater than compareAtPrice for Variant ID ${variant.node.id}`
      )
    );
    compareAtPrice = '';
  }

  return [displayPrice, compareAtPrice];
}

function getSkuPrefix(variant: ShopifyBuy.ProductVariant): string {
  const sku = variant?.node.sku;
  if (!sku) return '';

  if (sku.startsWith('AB')) {
    return sku;
  }

  if (sku.startsWith('SUP')) {
    return sku.substring(0, 5);
  }

  return sku.substring(0, 3);
}

export const Recommendation: React.FC<Props> = ({
  product,
  variant,
  sellingPlanId,
  discountCode,
  onChange,
}) => {
  if (!product || !variant) {
    return null;
  }

  const image = getImage(product, variant);
  const [displayPrice, compareAtPrice] = getPrice(variant);

  const skuPrefix = getSkuPrefix(variant);

  return (
    <li key={`${product.id}:${variant.node.id}`}>
      <div
        className={'recommended-item'}
        role={'group'}
        aria-label={'Recommended product'}
      >
        <div className="recommended-item__image">
          {product?.id === 'gid://shopify/Product/7844202905660' ? (
            <img
              src={
                'https://front.embarkvet.com/img/embark-on-call/eocVetImage_v1.png'
              }
              alt={''}
            />
          ) : (
            <img src={image?.src} alt={''} />
          )}
        </div>
        <div className="recommended-item__details">
          <h6>
            {product?.id === 'gid://shopify/Product/7844202905660' ? (
              <a
                href={'https://shop.embarkvet.com/pages/on-call-service'}
                data-heap-action={'Cart Upsell: Title Link'}
                data-heap-value={skuPrefix}
              >
                Embark Virtual Pet Care
              </a>
            ) : (
              <a
                href={`https://shop.embarkvet.com/products/${product.handle}`}
                data-heap-action={'Cart Upsell: Title Link'}
                data-heap-value={skuPrefix}
              >
                {product?.title}
              </a>
            )}
          </h6>

          {product?.variants.edges.length > 1 &&
            variant.node.title !== product.title && (
              <p className={'recommended-item__variant-title'}>
                {variant.node.title}
              </p>
            )}

          {product?.id === 'gid://shopify/Product/7844202905660' && (
            <div>
              <p>Online vet visits & dog training</p>
            </div>
          )}

          {product?.id !== 'gid://shopify/Product/7844202905660' && (
            <p className={'recommended-item__prices'}>
              <span className={'sr-only'}>Current Price: </span>
              <span
                dangerouslySetInnerHTML={{
                  __html: window.Shopify.formatMoney(
                    parseFloat(displayPrice),
                    formatMoneyWithConditionalCents(parseFloat(displayPrice))
                  ),
                }}
              ></span>
              {compareAtPrice && (
                <>
                  <span className={'sr-only'}>Regular Price: </span>
                  <span>
                    <del
                      dangerouslySetInnerHTML={{
                        __html: window.Shopify.formatMoney(
                          parseFloat(compareAtPrice),
                          formatMoneyWithConditionalCents(
                            parseFloat(compareAtPrice)
                          )
                        ),
                      }}
                    ></del>
                  </span>
                </>
              )}
            </p>
          )}
        </div>
        <div className="recommended-item__actions">
          <AddToCart
            onDone={() => {
              onChange && onChange();
              document.dispatchEvent(
                new CustomEvent('cart:atc:recommended', {
                  detail: {
                    product,
                  },
                })
              );
            }}
            variantId={variant.node.id}
            sellingPlanId={
              sellingPlanId
                ? `gid://shopify/SellingPlan/${sellingPlanId}`
                : undefined
            }
            discountCode={discountCode}
            ariaLabel={`Add ${product.title} ${variant.node.title} to your cart`}
            data-heap-action={'Cart Upsell: Add to Cart'}
            data-heap-value={skuPrefix}
          >
            + Add
          </AddToCart>
        </div>

        {product?.id === 'gid://shopify/Product/7844202905660' && (
          <div className="recommended-item__subcopy">
            <p>1 month FREE then $11.99/month</p>
          </div>
        )}
      </div>
    </li>
  );
};
